import React, { useState, useEffect } from "react";

interface GroupIconProps {
  groupId: string;
  iconUrl?: string;
}

const GroupIcon: React.FC<GroupIconProps> = ({ groupId, iconUrl }) => {
  const [svgContent, setSvgContent] = useState<string>('');

  useEffect(() => {
    if (!iconUrl) {
      return;
    }

    let cancelled = false;

    fetch(iconUrl)
      .then(response => response.text())
      .then(svgText => {
        if (!cancelled) {
          // Create unique prefix for this SVG instance to prevent conflicts when multiple icons are rendered
          const uniquePrefix = `svg-${groupId.slice(0, 8)}`;
          
          // Hacer los IDs únicos para evitar conflictos cuando múltiples grupos comparten el mismo icono
          let uniqueSvg = svgText
            .replace(/id="([^"]+)"/g, `id="$1-${groupId}"`)
            .replace(/url\(#([^)]+)\)/g, `url(#$1-${groupId})`)
            .replace(/xlink:href="#([^"]+)"/g, `xlink:href="#$1-${groupId}"`);
          
          // Make CSS class names unique to prevent conflicts when multiple SVGs are rendered
          // Find all unique class names in the SVG
          const classMatches = uniqueSvg.match(/class="([^"]+)"/g) || [];
          const allClasses = new Set<string>();
          classMatches.forEach(match => {
            const classes = match.match(/class="([^"]+)"/)?.[1]?.split(/\s+/) || [];
            classes.forEach(cls => allClasses.add(cls));
          });
          
          // Replace class names in style tag and update references throughout the SVG
          allClasses.forEach(className => {
            if (className && !className.includes(uniquePrefix)) {
              const uniqueClassName = `${className}-${uniquePrefix}`;
              
              // Replace in style tag
              uniqueSvg = uniqueSvg.replace(
                new RegExp(`\\.${className}([^{]*)\\{`, 'g'),
                `.${uniqueClassName}$1{`
              );
              
              // Replace in class attributes
              uniqueSvg = uniqueSvg.replace(
                new RegExp(`class="([^"]*\\b)${className}(\\b[^"]*)"`, 'g'),
                (match, before, after) => {
                  const classes = match.match(/class="([^"]+)"/)?.[1]?.split(/\s+/) || [];
                  const updatedClasses = classes.map((cls: string) => 
                    cls === className ? uniqueClassName : cls
                  ).join(' ');
                  return `class="${updatedClasses}"`;
                }
              );
            }
          });
          
          // Ensure SVG internal styles are scoped and not overridden by external CSS
          // Add !important to internal style rules to prevent external CSS from overriding
          uniqueSvg = uniqueSvg.replace(
            /<style[^>]*>([\s\S]*?)<\/style>/gi,
            (_match: string, styleContent: string) => {
              // Add !important to all CSS rules in the style tag to prevent external overrides
              const protectedStyles = styleContent
                .replace(/([^{}]+)\{([^{}]+)\}/g, (_ruleMatch: string, selector: string, declarations: string) => {
                  // Add !important to each declaration
                  const protectedDeclarations = declarations
                    .split(';')
                    .map((decl: string) => {
                      const trimmed = decl.trim();
                      if (!trimmed || trimmed.includes('!important')) {
                        return trimmed;
                      }
                      return trimmed + ' !important';
                    })
                    .filter((d: string) => d)
                    .join('; ');
                  return `${selector.trim()}{${protectedDeclarations}}`;
                });
              return `<style type="text/css" scoped>${protectedStyles}</style>`;
            }
          );
          
          setSvgContent(uniqueSvg);
        }
      })
      .catch(err => {
        if (!cancelled) {
          console.error('Error loading icon:', err);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [iconUrl, groupId]);

  if (!svgContent) {
    return null;
  }

  return (
    <div 
      className="w-full h-full flex items-center justify-center [&_svg]:w-full [&_svg]:h-full [&_svg]:max-w-full [&_svg]:max-h-full"
      style={{
        isolation: 'isolate' // Prevent CSS inheritance that might modify stroke/fill
      }}
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  );
};

GroupIcon.displayName = 'GroupIcon';

export default GroupIcon;
